import React, { useState, useEffect, useRef, useMemo } from 'react';
import { UpdateNotification } from '@/components/UpdateNotification';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from '@/contexts/TranslationContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useRoleDetection } from '@/hooks/useRoleDetection';
import { User } from '../types/user';
import { Button } from '@/components/ui/button';
import {
  Users,
  ClipboardList,
  Settings,
  ChevronDown,
  TrendingUp,
  X,
  Bell,
  CheckSquare,
  Calendar,
  ClipboardCheck,
  LayoutDashboard,
  Trash2,
  BarChart2,
  Wrench,
  GraduationCap,
  Vault,
  FileText,
  Plus,
  MessageSquare,
  Sparkles,
} from '@/components/ui/icons';
import {
  LogOut,
  Home,
  User2,
  ChefHat,
  ShieldCheck,
  CalendarDays,
  HelpCircle,
  Phone,
  LucideIcon,
  Building2,
  Menu as MenuIcon,
  Gift,
  Link as LinkIcon,
  Rocket,
  Star,
  ClipboardPen
} from 'lucide-react';
import { useShiftReportsAccess } from '@/hooks/useShiftReportsAccess';
import { useSetup } from '@/contexts/SetupContext';
import SetupBar from '@/components/setup/SetupBar';
import SetupTaskBar from '@/components/setup/SetupTaskBar';
import AssigneeIntroDialog from '@/components/setup/AssigneeIntroDialog';
import { setupChecklistService } from '@/services/setupChecklistService';
import { useWalkthrough } from '@/contexts/WalkthroughContext';
import WalkthroughBar from '@/components/walkthrough/WalkthroughBar';
import WelcomeTourDialog from '@/components/walkthrough/WelcomeTourDialog';
import { SETUP_MODULE_LIST, RESOLVED_STATES } from '@/lib/setupModules';
import { cn } from '@/lib/utils';
import api from '@/lib/axios';
import axios from 'axios';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel
} from "@/components/ui/dropdown-menu";
import { MobileNav } from './MobileNav';
import { useNotification } from '@/contexts/NotificationContext';
import { NotificationList } from './NotificationList';
import { handleAutoResubscribeAfterUpdate } from '@/utils/notificationPermission';
import { hasKitchenAccess } from '@/utils/departmentAccess';
import { shouldShowDevelopmentMenu } from '@/utils/leadershipNavAccess';
import StoreSelector from './StoreSelector';
import { Toaster } from '@/components/ui/toaster';
import { Toaster as SonnerToaster } from 'sonner';
import { useIOSKeyboardDetection } from '@/hooks/useIOSKeyboardDetection';
import { usePWADetection } from '@/hooks/usePWADetection';
import MobileRefreshButton from './MobileRefreshButton';
import { useQuery } from '@tanstack/react-query';
import { userPreferencesService } from '@/lib/services/userPreferences';
import { QuickActionsEditor, QUICK_ACTION_OPTIONS } from './QuickActionsEditor';
import { settingsService } from '@/lib/services/settings';
import { isEquipmentPageOn } from '@/lib/equipmentPages';
import { useRewardsSystem } from '@/hooks/useRewardsSystem';
import SendTaskDialog from '@/components/tasks/SendTaskDialog';
import { useSendTaskParam } from '@/components/tasks/useSendTaskParam';
import CoachDialog from '@/components/dashboard/CoachDialog';
import { useCoachParam } from '@/components/dashboard/useCoachParam';
import { usePersonalTasksEnabled } from '@/hooks/usePersonalTasksEnabled';
import { useRewardsCurrency } from '@/hooks/useRewardsCurrency';
import { applyCurrencyName } from '@/lib/rewardsCurrency';

interface MenuItem {
  icon: LucideIcon;
  label: string;
  href: string;
  show: boolean;
  badge: string | null;
  color?: string;
  submenu?: {
    icon: LucideIcon;
    label: string;
    href: string;
    badge: string | null;
    color?: string;
  }[];
}

export default function Layout({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth() as { user: User | null, logout: () => void };
  const { isDirector, isLeader, isTrainer, isTeamMember } = useRoleDetection();

  // Check if user is a Director (only Directors can access vendors)
  const canAccessVendors = isDirector;

  // Check if this is a leadership page that needs special layout behavior (no container padding)
  const isCharacteristicsPage = location.pathname.includes('/leadership/development-plans') ||
    location.pathname.includes('/leadership/360-evaluations') ||
    location.pathname.includes('/leadership/leadership-development') ||
    location.pathname.includes('/leadership/dashboard');
  // Hide bottom mobile menu on chat pages
  const isChatRoute = location.pathname.includes('/chat');
  // Test View for Setup Sheets should use full width and minimal padding
  const isSetupTestView = location.pathname.includes('/setup-view/') && location.pathname.endsWith('/test');
  // Print View for Setup Sheets renders its own paper-sized layout; treat
  // it the same as the test view so the global container/padding don't
  // interfere with the print stylesheet.
  const isSetupPrintView = location.pathname.includes('/setup-view/') && location.pathname.endsWith('/print');
  const { t } = useTranslation();
  const { showNotification, badgeCount, decreaseBadgeCount } = useNotification();
  const { data: settings } = useQuery({ queryKey: ['settings'], queryFn: settingsService.getSettings });
  const { data: userPreferences } = useQuery({ queryKey: ['userPreferences'], queryFn: userPreferencesService.getUserPreferences });
  const { showNav: showShiftReports } = useShiftReportsAccess();
  const { isEnabled: isRewardsEnabled } = useRewardsSystem();
  const currency = useRewardsCurrency();

  // Initialize iOS keyboard detection for the entire app
  useIOSKeyboardDetection();

  // Initialize PWA detection for the entire app
  usePWADetection();
  const { isFeatureEnabled } = useSubscription();
  // isSetupActive intentionally unused here — the checklist drives setup UI now.
  const { run: setupRun, setupState } = useSetup();
  const walkthrough = useWalkthrough();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // The checklist is the setup path now, so the nav pill has to be driven by IT,
  // not by the old wizard's setup.status. Gating on isSetupActive was a real bug:
  // a store whose wizard status had flipped to 'complete' lost the pill entirely
  // and had no way back to /setup except typing the URL.
  //
  // Enabled for everyone, not just admins — a leader handed a section needs the
  // way back just as much. Someone with no setup work gets an empty payload and
  // no pill, at the cost of one small cached request.
  const { data: checklist } = useQuery({
    queryKey: ['setup-checklist'],
    queryFn: setupChecklistService.get,
    enabled: Boolean(user),
    staleTime: 5 * 60_000
  });

  const checklistTasks = useMemo(() => Object.values(checklist?.tasks ?? {}), [checklist]);
  // Unfinished work is the whole condition — nothing to do with wizard state.
  const setupUnfinished = checklistTasks.length > 0 && checklistTasks.some((t) => !t.done);

  // Overall setup completion % for the nav pill.
  const setupPct = useMemo(() => {
    // Prefer checklist progress. A store working the checklist may never resolve
    // a single module, and reading modules would show them stuck near 0% while
    // their actual list is most of the way done.
    const tasks = checklist?.tasks;
    if (tasks && Object.keys(tasks).length) {
      const all = Object.values(tasks);
      return Math.round((all.filter((t) => t.done).length / all.length) * 100);
    }
    const mods = setupState?.modules;
    if (!mods) return 0;
    const sections = SETUP_MODULE_LIST.filter(
      (m) => m.key !== 'team_roster' && (!m.feature || isFeatureEnabled(m.feature as any))
    );
    const total = sections.length + 1; // + team roster
    const isDone = (k: string) => RESOLVED_STATES.includes((mods as any)[k]?.state ?? 'not_started');
    const done = sections.filter((m) => isDone(m.key)).length + (isDone('team_roster') ? 1 : 0);
    return Math.round((done / total) * 100);
  }, [setupState, checklist, isFeatureEnabled]);

  // First login for a store mid-onboarding: land admins on the checklist once
  // per session instead of the empty-feeling dashboard. Note: '/' itself never
  // renders Layout (HomePageRedirect forwards to a dashboard first), so the
  // check must match the actual landing pages.
  //
  // Driven by unfinished checklist work, NOT the wizard's setup.status — a store
  // whose wizard had been marked complete would otherwise never be brought here,
  // which is exactly what happened on the test store.
  useEffect(() => {
    const homePaths = ['/', '/dashboard', '/leadership/dashboard'];
    if (
      setupUnfinished &&
      // Assignees get the AssigneeIntroDialog instead — don't yank a leader who
      // was handed one section into the admin's full list.
      checklist?.role === 'admin' &&
      !setupRun &&                 // never yank the user mid-tour (the run drives navigation)
      homePaths.includes(location.pathname) &&
      !sessionStorage.getItem('ldg-setup-welcomed')
    ) {
      sessionStorage.setItem('ldg-setup-welcomed', '1');
      // The checklist is where an admin's setup actually happens — the tours are
      // launched from its items. Landing them on the tour hub instead buries the
      // decisions (timezone, permissions, alert recipients) that only live here.
      navigate('/setup');
    }
  }, [setupUnfinished, checklist?.role, setupRun, location.pathname, navigate]);
  const [isQuickActionsEditorOpen, setIsQuickActionsEditorOpen] = useState(false);
  const { open: sendTaskOpen, presetAssignee, close: closeSendTask } = useSendTaskParam();
  const { open: coachOpen, personId: coachPersonId, close: closeCoach } = useCoachParam();
  const personalTasksOn = usePersonalTasksEnabled();
  const [, setHasNotifications] = useState(false);
  const [pendingEvaluations] = useState(0);
  const [, setShowNotifications] = useState(false);
  const [, setUpcomingEvaluations] = useState<any[]>([]);
  const notificationRef = useRef<HTMLDivElement>(null);
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);
  const [notificationDropdownOpen, setNotificationDropdownOpen] = useState(false);


  const menuRef = useRef<HTMLDivElement>(null);



  // Simple mobile refresh using native browser refresh
  // This replaces the complex pull-to-refresh that was causing issues

  // Close notifications when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch initial counts and notification status
  useEffect(() => {
    const fetchData = async () => {
      try {
        const dashboardResponse = await api.get('/api/dashboard/stats');
        // If there are any unread notifications, they will be in upcomingEvaluations
        const notifications = dashboardResponse.data.upcomingEvaluations || [];
        setUpcomingEvaluations(notifications);
        // Set hasNotifications directly based on whether there are any notifications
        setHasNotifications(notifications.length > 0);
      } catch (error: any) {
        const _castErr = error as { response?: { status?: number; data?: { message?: string; [k: string]: unknown }; }; message?: string; code?: string; config?: any; };
        console.error('Error fetching dashboard data:', error);

        let errorMessage = "Failed to load notifications";
        if (axios.isAxiosError(error)) {
          if (_castErr.response?.data?.message) {
            errorMessage = _castErr.response.data.message;
          } else if (!_castErr.response) {
            errorMessage = "Server not responding. Please check your connection.";
          }
        }

        showNotification('error', 'Error', errorMessage);
      }
    };

    if (user?.store?._id) {
      fetchData();
      // Refresh every 5 minutes
      const interval = setInterval(fetchData, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [user?.store?._id, showNotification]);



  // Enhanced click outside handler with proper event propagation
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      // Only handle clicks if the menu is open
      if (!isMobileMenuOpen) return;

      // Check if click is outside both the menu and the toggle button
      const menuButton = document.querySelector('[data-mobile-menu-toggle]');
      const isClickOnButton = menuButton?.contains(event.target as Node);
      const isClickInMenu = menuRef.current?.contains(event.target as Node);

      if (!isClickInMenu && !isClickOnButton) {
        setIsMobileMenuOpen(false);
      }
    }

    // Add capture phase listener to handle events before they reach other handlers
    document.addEventListener('mousedown', handleClickOutside, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [isMobileMenuOpen]);

  // Toggle menu with proper event handling
  const handleMenuToggle = (event: React.MouseEvent) => {
    // Prevent event from bubbling to document
    event.preventDefault();
    event.stopPropagation();
    setIsMobileMenuOpen(prev => !prev);

    // Add a small delay to ensure the click event is fully processed
    setTimeout(() => {
      // Force a re-render to ensure the menu is properly displayed
      setIsMobileMenuOpen(prev => prev);
    }, 10);
  };

  // Check if user has full access (not restricted)
  const hasFullAccess = user?.accessLevel !== 'leadership-only' && user?.accessLevel !== 'limited';

  // Memoize menuItems to prevent unnecessary re-renders
  const menuItems: MenuItem[] = useMemo(() => [
    {
      icon: Home,
      label: t('navigation.dashboard'),
      href: '/',
      show: true, // Home is always shown
      badge: null
    },
    // Only include FOH if the feature is enabled
    ...(isFeatureEnabled('fohTasks') ? [{
      icon: CheckSquare,
      label: 'FOH',
      href: '/foh',
      show: true,
      badge: null,
      submenu: [
        {
          icon: ClipboardCheck,
          label: 'Daily Tasks',
          href: '/foh',
          badge: null
        },
        {
          icon: Sparkles,
          label: 'Cleaning & Maintenance',
          href: '/foh/cleaning',
          badge: null
        },
        // Store admins can hide the Equipment page (Settings → FOH)
        ...(isEquipmentPageOn(settings, 'foh') ? [{
          icon: Wrench,
          label: 'Equipment',
          href: '/foh/equipment',
          badge: null
        }] : []),
        ...(settings?.fohFoodSafety?.enabled ? [{
          icon: ShieldCheck,
          label: 'Food Safety',
          href: '/foh/food-safety',
          badge: null
        }] : []),
        // Director Board — per-store switch (Settings → Directors), Directors and above only
        ...(settings?.directorBoard?.enabled && (isDirector || user?.role === 'admin') ? [{
          icon: Star,
          label: 'Directors',
          href: '/foh/directors',
          badge: null
        }] : [])
      ]
    }] : []),
    // Only include Setup Sheet if the feature is enabled and allowed by settings
    ...(isFeatureEnabled('setups') && (
      !settings?.userAccess?.setupView?.leadersOnly || isLeader || user?.role === 'admin'
    ) ? [{
      icon: CalendarDays,
      label: 'Setup Sheet',
      href: (isLeader || user?.role === 'admin') ? '/setup-sheet-templates' : '/saved-setups',
      show: true,
      badge: null,
      ...((isLeader || user?.role === 'admin') ? {
        submenu: [
          {
            icon: LayoutDashboard,
            label: 'Templates',
            href: '/setup-sheet-templates',
            badge: null
          },
          {
            icon: Plus,
            label: 'New Setup',
            href: '/setup-sheet-builder',
            badge: null
          },
          {
            icon: Calendar,
            label: 'Saved Setups',
            href: '/saved-setups',
            badge: null
          },
          {
            icon: FileText,
            label: 'Shift Summary',
            href: '/shift-summary',
            badge: null
          }
        ]
      } : {})
    }] : []),
    // Shift Reports add-on — /me decides: store has it on AND (position has a set OR admin)
    ...(showShiftReports ? [{
      icon: ClipboardPen,
      label: 'Shift Reports',
      href: '/shift-reports',
      show: true,
      badge: null
    }] : []),
    // ServSafe add-on — per-store switch, every level
    ...(settings?.userAccess?.servSafe?.enabled ? [{
      icon: ShieldCheck,
      label: 'ServSafe',
      href: '/servsafe',
      show: true,
      badge: null
    }] : []),
    // Only include Kitchen if the feature is enabled and user is in Kitchen department
    ...(isFeatureEnabled('kitchen') && hasKitchenAccess(user) ? [{
      icon: ChefHat,
      label: t('navigation.kitchen'),
      href: '/kitchen',
      show: true,
      badge: null,
      submenu: [
        {
          icon: LayoutDashboard,
          label: t('navigation.dashboard'),
          href: '/kitchen',
          badge: null
        },
        {
          icon: BarChart2,
          label: t('navigation.analytics'),
          href: '/kitchen/waste-tracker/analytics',
          badge: null
        },
        // Store admins can hide the Equipment page (Settings → Kitchen)
        ...(isEquipmentPageOn(settings, 'kitchen') ? [{
          icon: Wrench,
          label: t('navigation.equipmentStatus'),
          href: '/kitchen/equipment',
          badge: null
        }] : []),
        {
          icon: ShieldCheck,
          label: t('navigation.foodSafety'),
          href: '/kitchen/food-safety',
          badge: null
        },
        // Per-store toggle, not a subscription feature — same gate as the
        // page, the tiles, and the Kitchen segmented nav.
        ...(settings?.userAccess?.selfAssessments?.enabled ? [{
          icon: ClipboardCheck,
          label: 'Self-Assessments',
          href: '/kitchen/self-assessments',
          badge: null
        }] : []),
        {
          icon: Sparkles,
          label: 'Cleaning & Maintenance',
          href: '/kitchen/cleaning',
          badge: null
        },
        {
          icon: ClipboardList,
          label: t('kitchen.shiftChecklists', 'Shift Checklists'),
          href: '/kitchen/checklists',
          badge: null
        },
        {
          icon: Trash2,
          label: t('navigation.wasteTracker'),
          href: '/kitchen/waste-tracker',
          badge: null
        }
      ]
    }] : []),
    // Borrow Log is deliberately NOT in this menu. Its entry points are the
    // dashboard quick-action tile and the mobile bottom nav (opt-in, per person).
    {
      icon: Users,
      label: isTeamMember ? t('navigation.me') : t('navigation.teamMembers'),
      href: isTeamMember ? `/users/${user?._id}` : '/users',
      show: hasFullAccess || isTeamMember || isTrainer, // Show for full access users, team members, or all trainers
      badge: null,
      submenu: (isTeamMember || (isTrainer && !isLeader)) ? [
        {
          icon: User2,
          label: t('navigation.myProfile'),
          href: `/users/${user?._id}`,
          badge: null
        },

        // Only show Training if the feature is enabled
        // Trainers see "Training" (they manage others), Team Members see "My Training"
        ...(isFeatureEnabled('training') ? [{
          icon: GraduationCap,
          label: isTrainer ? 'Training' : 'My Training',
          href: '/training',
          badge: null
        }] : []),

        // Only include Documentation if the feature is enabled
        ...(isFeatureEnabled('documentation') ? [{
          icon: FileText,
          label: 'My Documentation',
          href: `/documentation?employee=${user?._id}`,
          badge: null
        }] : []),

        // Only include Team Development if the leadership feature is enabled
        ...(isFeatureEnabled('leadership') && !((isTrainer && !isLeader) && user?.accessLevel === 'leadership-only') &&
          // Hide for Team Members/Trainers when Promotion Pathways feature is disabled
          ((isTeamMember || (isTrainer && !isLeader)) ? isFeatureEnabled('promotionPathways') : true)
          ? [{
            icon: TrendingUp,
            label: 'My Development',
            href: '/team-development/my-plans',
            badge: null
          }] : []),

        // Quick Links - available to everyone
        {
          icon: LinkIcon,
          label: 'Quick Links',
          href: '/quick-links',
          badge: null
        }
      ] : [
        {
          icon: Users,
          label: 'View All',
          href: '/users',
          badge: null
        },

        // Only show Documentation if the feature is enabled
        ...(isFeatureEnabled('documentation') ? [{
          icon: FileText,
          label: 'Documentation',
          href: '/documentation',
          badge: null
        }] : []),

        // Only include Evaluations if the feature is enabled and user is Level 3+ (Leader or Director)
        ...(isFeatureEnabled('evaluations') && isLeader ? [{
          icon: ClipboardList,
          label: 'Evaluations',
          href: '/evaluations',
          badge: pendingEvaluations > 0 ? pendingEvaluations.toString() : null,
          color: pendingEvaluations > 0 ? 'text-red-600' : undefined
        }] : []),

        // Team Surveys: leadership feature on and user is a Leader or above (each
        // survey decides who sees its results; the store can still limit it to admins)
        ...(isFeatureEnabled('leadership') && isLeader ? [{
          icon: ClipboardList,
          label: 'Team Surveys',
          href: '/team-surveys',
          badge: null
        }] : []),

        // Only show Training if the feature is enabled (accessible to all access levels)
        ...(isFeatureEnabled('training') ? [{
          icon: GraduationCap,
          label: 'Training',
          href: '/training',
          badge: null
        }] : []),

        // Quick Links - available to all users
        {
          icon: LinkIcon,
          label: 'Quick Links',
          href: '/quick-links',
          badge: null
        }
      ]
    },
    {
      icon: ClipboardList,
      label: 'Development',
      href: '#',
      // Show Development menu if user has access to any of its features AND is NOT a team member.
      // Trainers granted leadership access (accessLevel === 'leadership-only') are allowed through
      // so they can reach the Leadership link.
      show: shouldShowDevelopmentMenu({
        hasFullAccess,
        evaluationsEnabled: isFeatureEnabled('evaluations'),
        leadershipEnabled: isFeatureEnabled('leadership'),
        isTeamMember,
        isTrainer,
        isLeader,
        accessLevel: user?.accessLevel,
      }),
      badge: null,
      submenu: [
        // Only include 360 Evaluations if the feature is enabled and user is Level 3+ (Leader or Director)
        ...(isFeatureEnabled('evaluations') && isLeader ? [{
          icon: ClipboardList,
          label: '360 Evaluations',
          href: '/leadership/360-evaluations',
          badge: null
        }] : []),

        // Only include Leadership if the feature is enabled
        ...(isFeatureEnabled('leadership') ? [{
          icon: TrendingUp,
          label: 'Leadership',
          href: '/leadership',
          badge: null
        }] : []),

        // Only include Team Development if the leadership feature is enabled
        // Hide for Trainers with leadership-only access (they don't have backend access)
        ...(isFeatureEnabled('leadership') && !((isTrainer && !isLeader) && user?.accessLevel === 'leadership-only') &&
          // Hide for Trainers when Promotion Pathways is disabled
          ((isTrainer && !isLeader) ? isFeatureEnabled('promotionPathways') : true)
          ? [{
            icon: TrendingUp,
            label: 'Team Development',
            href: '/team-development/overview',
            badge: null
          }] : [])
      ]
    }
  ], [
    t,
    isFeatureEnabled,
    settings?.userAccess?.setupView?.leadersOnly,
    // Per-store page switches read inside the menu — without these the menu kept
    // its old entries until something else re-rendered it
    settings?.fohFoodSafety?.enabled,
    settings?.directorBoard?.enabled,
    settings?.userAccess?.servSafe?.enabled,
    showShiftReports,
    settings?.kitchenEquipment?.enabled,
    settings?.fohEquipment?.enabled,
    isLeader,
    isTrainer,
    isTeamMember,
    user?.position,
    user?._id,
    user?.role,
    user?.departments,
    user?.isAdmin,
    user?.email,
    user?.accessLevel,
    hasFullAccess,
    pendingEvaluations,
    isDirector
  ]);


  // Notifications are automatically enabled in AuthContext - no need to ask here
  // This is a work app and notifications are required

  // 🔔 AUTO-RESUBSCRIBE: Handle automatic re-subscription after service worker updates
  useEffect(() => {
    const handleAutoResubscribe = async () => {
      try {
        // Only attempt auto-resubscribe if user is logged in
        if (user?.store?._id) {
          await handleAutoResubscribeAfterUpdate();
        }
      } catch (error) {
      }
    };

    // Run auto-resubscribe check when user is logged in (silent)
    if (user?.store?._id) {
      handleAutoResubscribe();
    }

    // No cleanup needed since we removed the event listener
  }, [user?.store?._id]);

  const isChatPage = location.pathname.includes('/chat');

  return (
    <div className={`${isChatPage ? 'h-[100dvh] max-h-[100dvh] flex flex-col' : 'min-h-screen'} bg-white`}>

      {/* Mobile Header */}
      <header className={cn("min-[938px]:hidden mobile-header print:hidden")} data-layout-header="main">
        <div className="mobile-header-content">
          {/* Logo and Store Info */}
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity touch-manipulation"
          >
            <TrendingUp className="h-6 w-6 text-red-600" />
            <div className="flex flex-col">
              <span className="font-bold">LD Growth</span>
              <span className="text-xs text-gray-500">#{user?.store?.storeNumber || '00000'}</span>
            </div>
          </button>

          {/* Right side buttons */}
          <div className="flex items-center gap-1">
            {/* Mobile Refresh Button */}
            <MobileRefreshButton />

            {/* Mobile Menu Button */}
            <button
              data-mobile-menu-toggle
              data-tour="tm-mobile-menu"
              onClick={handleMenuToggle}
              className="min-[938px]:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200 min-h-[44px] min-w-[44px] touch-manipulation active:opacity-70 relative z-[201]"
              style={{ touchAction: 'manipulation' }}
            >
              <MenuIcon className="w-6 h-6" />
            </button>
          </div>
        </div>
      </header>

      {/* Desktop Navigation Bar */}
      <div className="hidden min-[938px]:block fixed top-0 left-0 right-0 bg-white border-b z-50 print:hidden pt-[env(safe-area-inset-top)] pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)]">
        <div className="h-16 mx-auto max-w-7xl px-4 flex items-center justify-between gap-8">
          {/* Left Section: Logo and Store Info */}
          <div className="flex items-center gap-8">
            {/* Logo and Store Info */}
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            >
              <TrendingUp className="h-6 w-6 text-red-600" />
              <div className="flex flex-col">
                <span className="font-bold">LD Growth</span>
                <span className="text-xs text-gray-500">#{user?.store?.storeNumber || '00000'}</span>
              </div>
            </button>

            {/* Desktop Navigation Items */}
            <div className="hidden min-[938px]:flex items-center gap-1">
              {menuItems
                .filter(item => item.show)
                .map(item => {
                  const Icon = item.icon;
                  const isActive = item.href === '/'
                    ? location.pathname === '/'
                    : location.pathname.startsWith(item.href);

                  if (item.submenu) {
                    return (
                      <DropdownMenu key={item.href}>
                        <DropdownMenuTrigger asChild>
                          <button
                            className={cn(
                              "px-4 py-2 flex items-center gap-2 rounded-xl min-h-[44px]",
                              (isActive || item.submenu.some(sub => location.pathname.startsWith(sub.href)))
                                ? "bg-red-50 text-red-600"
                                : "hover:bg-gray-50 text-gray-600"
                            )}
                          >
                            <Icon className="w-4 h-4" />
                            <span className="text-sm font-medium">{item.label}</span>
                            <ChevronDown className="w-4 h-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-56">
                          {item.submenu.map(subItem => {
                            const SubIcon = subItem.icon;
                            const isSubActive = location.pathname.startsWith(subItem.href);
                            return (
                              <DropdownMenuItem
                                key={subItem.href}
                                onClick={() => navigate(subItem.href)}
                                className={cn(
                                  "flex items-center gap-2 min-h-[44px] cursor-pointer",
                                  isSubActive ? "bg-red-50" : ""
                                )}
                              >
                                <SubIcon className={cn(
                                  "w-4 h-4",
                                  subItem.color || (isSubActive ? "text-red-600" : "text-gray-400")
                                )} />
                                <span className={cn(
                                  subItem.color || (isSubActive ? "text-red-600" : "text-gray-700")
                                )}>
                                  {subItem.label}
                                </span>
                                {subItem.badge && (
                                  <span className="ml-auto px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-600">
                                    {subItem.badge}
                                  </span>
                                )}
                              </DropdownMenuItem>
                            );
                          })}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    );
                  }

                  // Regular menu item without submenu
                  return (
                    <button
                      key={item.href}
                      onClick={() => navigate(item.href)}
                      className={cn(
                        "px-4 py-2 flex items-center gap-2 rounded-xl min-h-[44px]",
                        isActive ? "bg-red-50 text-red-600" : "hover:bg-gray-50 text-gray-600"
                      )}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="text-sm font-medium">{item.label}</span>
                    </button>
                  );
                })}
            </div>
          </div>

          {/* Right Section: Search and Actions */}
          <div className="flex items-center gap-4">
            {/* Always-available way back to the checklist. Navigation stays wide
                open during setup, which only works if returning is one visible
                click — so this shows on mobile too, where wandering off and
                having to type a URL hurts most. */}
            {setupUnfinished && (
              <button
                onClick={() => navigate('/setup')}
                title={`Back to your setup checklist — ${setupPct}% done`}
                aria-label={`Setup checklist, ${setupPct} percent done`}
                // shrink-0 + nowrap: without them this pill both wrapped onto two
                // lines and squeezed the nav enough to wrap "LD Growth" and
                // "Team Members". The word "Setup" only earns its space on a wide
                // header; the rocket and the number carry it everywhere else.
                className="flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-[#E51636]/30 bg-[#E51636]/5 px-2.5 py-1 text-xs font-semibold leading-none text-[#E51636] transition-colors hover:bg-[#E51636]/10"
              >
                <Rocket className="h-3.5 w-3.5 shrink-0" />
                <span className="hidden min-[1180px]:inline">Setup</span>
                <span className="tabular-nums">{setupPct}%</span>
              </button>
            )}
            {/* Notifications */}
            <div className="relative" ref={notificationRef}>
              <DropdownMenu open={notificationDropdownOpen} onOpenChange={setNotificationDropdownOpen}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="relative"
                    aria-label={t('common.notifications', 'Notifications')}
                  >
                    <Bell className="w-5 h-5" />
                    {badgeCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-[#E51636] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                        {badgeCount}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-[380px] max-h-[70vh] overflow-hidden p-0 shadow-lg border border-gray-200">
                  <div className="p-4 border-b border-gray-100 bg-gray-50">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-gray-900">{t('common.notifications', 'Notifications')}</h3>
                      {badgeCount > 0 && (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-600">
                          {badgeCount} new
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="overflow-y-auto max-h-[60vh]">
                    <NotificationList onDismiss={() => {
                      // Close the dropdown and decrease badge count
                      setNotificationDropdownOpen(false);
                      decreaseBadgeCount();
                    }} />
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 hover:opacity-80 transition-opacity min-h-[44px]">
                  <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                    <User2 className="w-4 h-4 text-gray-600" />
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 max-h-[var(--radix-dropdown-menu-content-available-height)] overflow-y-auto">
                {/* Store Switcher - for multi-store users */}
                <StoreSelector variant="dropdown" />

                {/* My Profile - available to everyone */}
                <DropdownMenuItem
                  onClick={() => navigate(`/users/${user?._id}`)}
                  className="flex items-center gap-2 min-h-[44px] cursor-pointer"
                >
                  <User2 className="w-4 h-4 text-gray-400" />
                  <span>{t('navigation.myProfile')}</span>
                </DropdownMenuItem>

                {/* Take the tour - every role has a journey (admins get the orientation) */}
                {walkthrough.isEligible && (
                  <DropdownMenuItem
                    onClick={() => walkthrough.startRun()}
                    className="flex items-center gap-2 min-h-[44px] cursor-pointer"
                  >
                    <Sparkles className="w-4 h-4 text-gray-400" />
                    <span>Take the tour</span>
                  </DropdownMenuItem>
                )}

                {/* Operator Overview - for directors, admins, and super admin */}
                {(isDirector || user?.isAdmin || user?.email === 'jonp4208@gmail.com') && (
                  <DropdownMenuItem
                    onClick={() => navigate('/operator-overview')}
                    className="flex items-center gap-2 min-h-[44px] cursor-pointer"
                  >
                    <Building2 className="w-4 h-4 text-gray-400" />
                    <span>Weekly Digest</span>
                  </DropdownMenuItem>
                )}

                {/* Communication */}
                <DropdownMenuItem
                  onClick={() => navigate('/chat')}
                  className="flex items-center gap-2 min-h-[44px] cursor-pointer"
                >
                  <MessageSquare className="w-4 h-4 text-gray-400" />
                  <span>Team Chat</span>
                </DropdownMenuItem>

                {/* Calendar - Leadership subscription required */}
                {isFeatureEnabled('leadership') && (
                  <DropdownMenuItem
                    onClick={() => navigate('/calendar')}
                    className="flex items-center gap-2 min-h-[44px] cursor-pointer"
                  >
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span>Calendar</span>
                  </DropdownMenuItem>
                )}

                {/* Operations */}
                {isLeader && (
                  <DropdownMenuItem
                    onClick={() => navigate('/customer-complaints')}
                    className="flex items-center gap-2 min-h-[44px] cursor-pointer"
                  >
                    <Phone className="w-4 h-4 text-gray-400" />
                    <span>Guest Recovery</span>
                  </DropdownMenuItem>
                )}

                {/* Vendors - Directors only */}
                {canAccessVendors && (
                  <DropdownMenuItem
                    onClick={() => navigate('/vendors')}
                    className="flex items-center gap-2 min-h-[44px] cursor-pointer"
                  >
                    <Building2 className="w-4 h-4 text-gray-400" />
                    <span>Vendors</span>
                  </DropdownMenuItem>
                )}

                {/* Safe Counting - Leaders and above (only if enabled) */}
                {settings?.safeConfig?.enabled && (isLeader || user?.isAdmin) && (
                  <DropdownMenuItem
                    onClick={() => navigate('/safe-counting')}
                    className="flex items-center gap-2 min-h-[44px] cursor-pointer"
                  >
                    <Vault className="w-4 h-4 text-gray-400" />
                    <span>Safe Counting</span>
                  </DropdownMenuItem>
                )}

                {/* CFAdollars Rewards */}
                {isRewardsEnabled && (
                  <DropdownMenuItem
                    onClick={() => navigate('/rewards')}
                    className="flex items-center gap-2 min-h-[44px] cursor-pointer"
                  >
                    <Gift className="w-4 h-4 text-gray-400" />
                    <span>{currency}</span>
                  </DropdownMenuItem>
                )}

                {/* Admin */}
                <DropdownMenuItem
                  onClick={() => navigate('/settings')}
                  className="flex items-center gap-2 min-h-[44px] cursor-pointer"
                >
                  <Settings className="w-4 h-4 text-gray-400" />
                  <span>{t('navigation.settings')}</span>
                </DropdownMenuItem>

                {/* Support */}
                <DropdownMenuItem
                  onClick={() => navigate('/how-to')}
                  className="flex items-center gap-2 min-h-[44px] cursor-pointer"
                >
                  <HelpCircle className="w-4 h-4 text-gray-400" />
                  <span>Help & Support</span>
                </DropdownMenuItem>

                {/* Admin link - only visible to Jonathon Pope */}
                {user?.email === 'jonp4208@gmail.com' && (
                  <>
                    <DropdownMenuLabel className="font-medium text-xs text-gray-500 mt-1">
                      Admin
                    </DropdownMenuLabel>
                    <DropdownMenuItem
                      onClick={() => navigate('/admin')}
                      className="flex items-center gap-2 min-h-[44px] cursor-pointer"
                    >
                      <ShieldCheck className="w-4 h-4 text-gray-400" />
                      <span>Admin Dashboard</span>
                    </DropdownMenuItem>

                    <DropdownMenuLabel className="font-medium text-xs text-gray-500 mt-1">
                      Developer
                    </DropdownMenuLabel>
                  </>
                )}

                <DropdownMenuItem
                  onClick={logout}
                  className="flex items-center gap-2 min-h-[44px] cursor-pointer text-red-600"
                >
                  <LogOut className="w-4 h-4" />
                  <span>{t('auth.logout')}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className={`mobile-main ${isChatPage ? 'flex-1 flex flex-col overflow-hidden' : ''} ${isCharacteristicsPage || isSetupTestView || isSetupPrintView ? 'debug-no-min-height' : 'min-[938px]:min-h-screen'}`} style={isChatPage ? { flex: 1 } : {}}>
        {setupRun && <SetupBar />}
        {/* Checklist deep link: shows only while ?setupTask= is on the URL, so
            it can't collide with a wizard run or linger after navigation. */}
        {!setupRun && <SetupTaskBar />}
        {/* isEligible too: a leader logging into a tab where a TM abandoned a
            run mid-tour must not inherit the tour bar (sessionStorage survives
            the hard-redirect logout). */}
        {walkthrough.run && walkthrough.isEligible && <WalkthroughBar />}
        <WelcomeTourDialog />
        <AssigneeIntroDialog />
        {(() => {
          const isChatPageLocal = location.pathname.includes('/chat');
          const isUsersPage = location.pathname === '/users';
          const isTrainingPage = location.pathname.startsWith('/training');
          const isMyLeadersPage = location.pathname.includes('/leadership/my-leaders');
          const noContainer = isSetupTestView || isSetupPrintView || location.pathname.includes('/documentation/') || location.pathname.includes('/foh') || location.pathname.includes('/leadership/development-plans') || isCharacteristicsPage || isChatPageLocal || isTrainingPage || location.pathname.includes('/how-to') || location.pathname.startsWith('/admin/stores') || isMyLeadersPage;
          const noPagePadding = isSetupTestView || isSetupPrintView || location.pathname.includes('/foh') || location.pathname.includes('/leadership/development-plans') || isCharacteristicsPage || isChatPageLocal || isUsersPage || isTrainingPage || location.pathname.includes('/how-to') || location.pathname.startsWith('/admin/stores') || location.pathname.includes('/food-safety') || location.pathname.includes('/documentation/') || isMyLeadersPage;
          const noDesktopTopPad = isCharacteristicsPage || isChatPageLocal || location.pathname.includes('/leadership/plans/') || isUsersPage || isTrainingPage || isMyLeadersPage;
          return (
            <div className={`${isChatPageLocal ? 'flex-1 flex flex-col overflow-hidden min-[938px]:pt-16' : ''} ${noContainer ? '' : 'max-w-7xl mx-auto'} ${noPagePadding ? '' : 'py-4 min-[938px]:p-6'} ${noDesktopTopPad ? '' : 'min-[938px]:pt-24'}`} data-layout-content-wrapper={true} data-chat-route={isChatPageLocal ? 'true' : undefined}>
              {children}
            </div>
          );
        })()}
      </main>

      {/* Update Notification */}
      <UpdateNotification className="print:hidden" />

      {/* Mobile Menu Dropdown */}
      {isMobileMenuOpen && (
        <div
          className="min-[938px]:hidden fixed inset-0 z-[1050] overflow-hidden print:hidden"
        >
          {/* Backdrop with animation and blur */}
          <div 
            className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-in fade-in duration-300"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <div
            ref={menuRef}
            className="absolute right-0 top-0 bottom-0 w-full sm:w-[400px] bg-white shadow-2xl flex flex-col overscroll-contain animate-in slide-in-from-right-[100%] duration-300 ease-out"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b flex items-center justify-between shrink-0 bg-white relative z-10 pt-[max(1rem,env(safe-area-inset-top))]">
              <button
                onClick={() => {
                  navigate(`/users/${user?._id}`);
                  setIsMobileMenuOpen(false);
                }}
                className="flex items-center gap-3 -m-2 p-2 rounded-xl hover:bg-gray-50 active:opacity-70 transition-colors text-left touch-manipulation"
                aria-label={t('navigation.myProfile')}
              >
                <div className="w-8 h-8 rounded-full bg-red-50 flex items-center justify-center">
                  <User2 className="w-4 h-4 text-red-600" />
                </div>
                <div>
                  <div className="font-semibold text-sm text-gray-900">{user?.name}</div>
                  <div className="text-xs text-gray-500">{user?.position}</div>
                </div>
              </button>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors duration-200"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="flex-1 p-2 overflow-y-auto thin-scrollbar" style={{ overscrollBehavior: 'contain', paddingBottom: 'calc(var(--safe-area-bottom, 0px) + 2rem)' }}>
              {/* Store Switcher - right under the name for multi-store users */}
              <div className="mb-3">
                <StoreSelector variant="dropdown" />
              </div>

              {/* Notifications Section - Integrated into main scroll */}
              {badgeCount > 0 && (
                <>
                  <div className="flex items-center justify-between mb-2 px-1">
                    <h3 className="font-medium text-gray-900 text-sm">{t('common.notifications', 'Notifications')}</h3>
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-600">
                      {badgeCount} new
                    </span>
                  </div>
                  <div className="bg-gray-50 rounded-lg border border-gray-200 mb-4">
                    <NotificationList
                      onDismiss={() => {
                        // Optimistically decrease badge count immediately
                        decreaseBadgeCount();
                      }}
                      compact={true}
                      isMobile={true}
                    />
                  </div>
                </>
              )}

              {/* Quick Actions Grid (Bento Hub) */}
              <div className="px-3 mb-6 mt-2">
                <div className="flex items-center justify-between mb-3 ml-1 pr-1">
                  <div className="text-xs font-bold text-gray-400 uppercase tracking-wider">Quick Actions</div>
                  <button 
                    onClick={() => setIsQuickActionsEditorOpen(true)}
                    className="p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                  >
                    <Settings className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {(() => {
                    const savedKeys = userPreferences?.uiPreferences?.quickActions?.items
                       ?.filter(item => item.show)
                       ?.sort((a, b) => a.order - b.order)
                       // sendTask is in the default set so an opted-in store sees it without
                       // customising; the personalTasksOn check below drops it for everyone else.
                       // timeOff likewise; the lateTimeOff check below drops it for stores that never turned it on.
                       ?.map(item => item.key) || ['teamChat', 'sendTask', 'timeOff', 'calendar', 'guestRecovery', 'rewards', 'weeklyDigest', 'safeCounting'];
                    // Schedule requests are pinned for everyone once the store turns them
                    // on, even for people who customized this grid before it existed.
                    if (settings?.userAccess?.lateTimeOff?.enabled && !savedKeys.includes('timeOff')) savedKeys.unshift('timeOff');

                    // Map keys to valid options considering user access
                    const activeOptions = savedKeys.map(key => QUICK_ACTION_OPTIONS.find(opt => opt.id === key)).filter(Boolean) as typeof QUICK_ACTION_OPTIONS;

                    return activeOptions.map(option => {
                      // Perform fast permissions check so they don't render if permissions changed
                      if (option.requiresFeature && !isFeatureEnabled(option.requiresFeature)) return null;
                      if (option.id === 'rewards' && !isRewardsEnabled) return null;
                      if (option.id === 'sendTask' && !personalTasksOn) return null;
                      if (option.requiresLeader && !isLeader && !user?.isAdmin) return null;
                      if (option.requiresDirector && !isDirector && !user?.isAdmin && user?.email !== 'jonp4208@gmail.com') return null;
                      if (['kitchen', 'bohCleaning', 'kitchenChecklists', 'kitchenEquipment', 'foodSafety'].includes(option.id) && !hasKitchenAccess(user) && !isLeader) return null;
                      if (option.id === 'borrowLog' && !settings?.userAccess?.borrowLog?.enabled) return null;
                      if (option.id === 'timeOff' && !settings?.userAccess?.lateTimeOff?.enabled) return null;
                      if (option.id === 'servSafe' && !settings?.userAccess?.servSafe?.enabled) return null;
                      if (option.id === 'selfAssessments' && !settings?.userAccess?.selfAssessments?.enabled) return null;
                      if (option.id === 'kitchenEquipment' && !isEquipmentPageOn(settings, 'kitchen')) return null;

                      const Icon = option.icon;
                      
                      // Map standard navigation routes for the options
                      const routes: Record<string, string> = {
                        teamChat: '/chat',
                        // Must be /dashboard, not /. "/" redirects with an absolute
                        // <Navigate to="/dashboard">, which drops the query string, and "/"
                        // never renders Layout so the dialog isn't mounted there.
                        sendTask: '/dashboard?sendTask=1',
                        calendar: '/calendar',
                        guestRecovery: '/customer-complaints',
                        rewards: '/rewards',
                        weeklyDigest: '/operator-overview',
                        safeCounting: '/safe-counting',
                        foh: '/foh',
                        cleaning: '/foh/cleaning',
                        kitchen: '/kitchen',
                        bohCleaning: '/kitchen/cleaning',
                        kitchenChecklists: '/kitchen/checklists',
                        kitchenEquipment: '/kitchen/equipment',
                        foodSafety: '/kitchen/food-safety',
                        borrowLog: '/borrow-log',
                        timeOff: '/time-off?new=1',
                        servSafe: '/servsafe',
                        selfAssessments: '/kitchen/self-assessments',
                        wasteTracker: '/kitchen/waste-tracker',
                        setupSheet: '/saved-setups',
                        evaluations: '/evaluations',
                        training: '/training',
                        assessments: '/training/assessments',
                        documentation: '/documentation',
                        analytics: '/analytics',
                        team: '/users',
                        leadership: '/leadership',
                        teamDevelopment: '/team-development/overview',
                        vendors: '/vendors',
                        quickLinks: '/quick-links',
                        howTo: '/how-to'
                      };

                      return (
                        <button 
                          key={option.id}
                          onClick={() => { navigate(routes[option.id] || '/'); setIsMobileMenuOpen(false); }}
                          className="flex flex-col items-center justify-center p-5 bg-gray-50/80 hover:bg-red-50 text-gray-700 hover:text-red-600 rounded-2xl border border-gray-100 transition-all gap-3 active:scale-95"
                        >
                          <div className="w-10 h-10 rounded-full bg-white shadow-sm flex items-center justify-center border border-gray-100 group-hover:border-red-100">
                            <Icon className="w-5 h-5" />
                          </div>
                          <span className="font-semibold text-[13px] text-center leading-tight">{applyCurrencyName(option.label, currency)}</span>
                        </button>
                      );
                    });
                  })()}
                </div>
              </div>
              <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 ml-4">Navigation</div>
              {/* Menu Items */}
              {menuItems
                .filter(item => item.show)
                .map(item => {
                  const Icon = item.icon;
                  const isActive = item.href === '/'
                    ? location.pathname === '/'
                    : location.pathname.startsWith(item.href);

                  if (item.submenu) {
                    return (
                      <div key={item.href} className="mb-1">
                        <button
                          onClick={() => setActiveSubmenu(activeSubmenu === item.href ? null : item.href)}
                          className={cn(
                            "w-full px-3 py-2 flex items-center justify-between rounded-lg",
                            (isActive || item.submenu.some(sub => location.pathname.startsWith(sub.href)))
                              ? "bg-red-50 text-red-600"
                              : "hover:bg-gray-50 text-gray-700"
                          )}
                        >
                          <div className="flex items-center gap-3">
                            <Icon className="w-5 h-5" />
                            <span className="font-medium">{item.label}</span>
                          </div>
                          <ChevronDown className={cn(
                            "w-4 h-4 transition-transform",
                            activeSubmenu === item.href ? "rotate-180" : ""
                          )} />
                        </button>

                        <div 
                          className={cn(
                            "grid transition-all duration-300 ease-in-out",
                            activeSubmenu === item.href ? "grid-rows-[1fr] opacity-100 mt-1 mb-1" : "grid-rows-[0fr] opacity-0"
                          )}
                        >
                          <div className="overflow-hidden">
                            <div className="ml-4 pl-4 border-l-2 border-red-100 py-1">
                              {item.submenu.map(subItem => {
                              const SubIcon = subItem.icon;
                              const isSubActive = location.pathname.startsWith(subItem.href);
                              return (
                                <button
                                  key={subItem.href}
                                  onClick={() => {
                                    navigate(subItem.href);
                                    setIsMobileMenuOpen(false);
                                  }}
                                  className={cn(
                                    "w-full px-3 py-1.5 flex items-center gap-2 rounded-lg my-0.5 text-sm",
                                    isSubActive ? "bg-red-50 text-red-600" : "hover:bg-gray-50 text-gray-700"
                                  )}
                                >
                                  <SubIcon className="w-4 h-4" />
                                  <span>{subItem.label}</span>
                                  {subItem.badge && (
                                    <span className="ml-auto px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-600">
                                      {subItem.badge}
                                    </span>
                                  )}
                                </button>
                              );
                            })}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <button
                      key={item.href}
                      onClick={() => {
                        navigate(item.href);
                        setIsMobileMenuOpen(false);
                      }}
                      className={cn(
                        "w-full px-3 py-2 flex items-center gap-2 rounded-lg mb-0.5",
                        isActive ? "bg-red-50 text-red-600" : "hover:bg-gray-50 text-gray-700"
                      )}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="font-medium">{item.label}</span>
                      {item.badge && (
                        <span className="ml-auto px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-600">
                          {item.badge}
                        </span>
                      )}
                    </button>
                  );
                })}

              {/* Account Settings Section */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 ml-4">Account</div>
                
                {/* Team Chat */}
                <button
                  onClick={() => {
                    navigate('/chat');
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full px-3 py-2 flex items-center gap-2 rounded-lg mb-1 hover:bg-gray-50 text-gray-700"
                >
                  <MessageSquare className="w-5 h-5 text-gray-400" />
                  <span className="font-medium">Team Chat</span>
                </button>

                {/* Calendar - Leadership subscription required */}
                {isFeatureEnabled('leadership') && (
                  <button
                    onClick={() => {
                      navigate('/calendar');
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full px-3 py-2 flex items-center gap-2 rounded-lg mb-1 hover:bg-gray-50 text-gray-700"
                  >
                    <Calendar className="w-5 h-5 text-gray-400" />
                    <span className="font-medium">Calendar</span>
                  </button>
                )}

                {/* Guest Recovery - Leaders only */}
                {isLeader && (
                  <button
                    onClick={() => {
                      navigate('/customer-complaints');
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full px-3 py-2 flex items-center gap-2 rounded-lg mb-1 hover:bg-gray-50 text-gray-700"
                  >
                    <Phone className="w-5 h-5 text-gray-400" />
                    <span className="font-medium">Guest Recovery</span>
                  </button>
                )}

                {/* Safe Counting - Leaders and above (only if enabled) */}
                {settings?.safeConfig?.enabled && (isLeader || user?.isAdmin) && (
                  <button
                    onClick={() => {
                      navigate('/safe-counting');
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full px-3 py-2 flex items-center gap-2 rounded-lg mb-1 hover:bg-gray-50 text-gray-700"
                  >
                    <Vault className="w-5 h-5 text-gray-400" />
                    <span className="font-medium">Safe Counting</span>
                  </button>
                )}

                {/* CFAdollars */}
                {isRewardsEnabled && (
                  <button
                    onClick={() => {
                      navigate('/rewards');
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full px-3 py-2 flex items-center gap-2 rounded-lg mb-1 hover:bg-gray-50 text-gray-700"
                  >
                    <Gift className="w-5 h-5 text-gray-400" />
                    <span className="font-medium">{currency}</span>
                  </button>
                )}

                {/* Weekly Digest - Directors only */}
                {(isDirector || user?.isAdmin || user?.email === 'jonp4208@gmail.com') && (
                  <button
                    onClick={() => {
                      navigate('/operator-overview');
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full px-3 py-2 flex items-center gap-2 rounded-lg mb-1 hover:bg-gray-50 text-gray-700"
                  >
                    <Building2 className="w-5 h-5 text-gray-400" />
                    <span className="font-medium">Weekly Digest</span>
                  </button>
                )}

                {/* Vendors - Directors only */}
                {canAccessVendors && (
                  <button
                    onClick={() => {
                      navigate('/vendors');
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full px-3 py-2 flex items-center gap-2 rounded-lg mb-1 hover:bg-gray-50 text-gray-700"
                  >
                    <Building2 className="w-5 h-5 text-gray-400" />
                    <span className="font-medium">Vendors</span>
                  </button>
                )}

                {/* Admin */}
                <button
                  onClick={() => {
                    navigate('/settings');
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full px-3 py-2 flex items-center gap-2 rounded-lg mb-1 hover:bg-gray-50 text-gray-700"
                >
                  <Settings className="w-5 h-5 text-gray-400" />
                  <span className="font-medium">{t('navigation.settings')}</span>
                </button>

                {/* Support */}
                <button
                  onClick={() => {
                    navigate('/how-to');
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full px-3 py-2 flex items-center gap-2 rounded-lg mb-1 hover:bg-gray-50 text-gray-700"
                >
                  <HelpCircle className="w-5 h-5 text-gray-500" />
                  <span className="font-medium">Help & Support</span>
                </button>

                {/* Admin link - only visible to Jonathon Pope */}
                {user?.email === 'jonp4208@gmail.com' && (
                  <>
                    <div className="mt-3 mb-1 px-3">
                      <div className="text-xs font-medium text-gray-500">Admin</div>
                    </div>
                    <button
                      onClick={() => {
                        navigate('/admin');
                        setIsMobileMenuOpen(false);
                      }}
                      className="w-full px-3 py-2 flex items-center gap-2 rounded-lg mb-1 hover:bg-gray-50 text-gray-700"
                    >
                      <ShieldCheck className="w-5 h-5 text-gray-500" />
                      <span className="font-medium">Admin Dashboard</span>
                    </button>

                    <div className="mt-3 mb-1 px-3">
                      <div className="text-xs font-medium text-gray-500">Developer</div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Logout Footer - Pinned to bottom */}
            <div className="p-4 border-t border-gray-100 shrink-0 bg-gray-50 pb-[max(1rem,env(safe-area-inset-bottom))]">
              <button
                onClick={() => {
                  logout();
                  setIsMobileMenuOpen(false);
                }}
                className="w-full px-4 py-2.5 flex items-center justify-center gap-2 rounded-xl text-red-600 hover:bg-red-50 border border-red-100 bg-white shadow-sm transition-all"
              >
                <LogOut className="w-5 h-5" />
                <span className="font-semibold">{t('auth.logout')}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Navigation (hidden on chat pages) */}
      {!isChatRoute && (
        <div className="min-[938px]:hidden">
          <MobileNav />
        </div>
      )}

      {/* Global Toast Notifications */}
      <Toaster />
      {/* sonner is imported by ~28 pages (documentation, training, evaluations…). Without its
          own Toaster mounted, every one of those toast.error/success calls was a silent no-op —
          a failed write-up showed the user nothing at all. */}
      <SonnerToaster position="top-center" richColors closeButton />


      <QuickActionsEditor
        open={isQuickActionsEditorOpen}
        onOpenChange={setIsQuickActionsEditorOpen}
      />

      {/* A bookmarked ?sendTask=1 must open nothing at a store that opted out. */}
      <SendTaskDialog
        open={sendTaskOpen && personalTasksOn}
        presetAssignee={presetAssignee}
        onClose={closeSendTask}
      />

      {/* No per-store gate: the server-filtered picker just comes back empty
          for anyone with nobody below their level. */}
      <CoachDialog open={coachOpen} personId={coachPersonId} onClose={closeCoach} />

    </div>
  );
}
